import style from './checkBox.module.scss';
import type { InputProps, UiStateProps } from '.';
import { joinNames } from '../helpers';
import { useField } from 'formik';
import { ValidValueProps, useValidationFeedback } from '../../components/ValidationFeedbackFormik';
import { ErrorMessage } from '../Container';
import Label from '../Label';
import { useHelpBox } from '../HelpBox';

export type CheckBoxProps = Omit<InputProps, 'type'>;
export default function CheckBox({
    name,
    id,
    label,
    error,
    tabIndex = 0,
    className,
    helptext,
    style: outerStyle,
    ...attr
}: CheckBoxProps & UiStateProps) {
    const [HelpBox, HelpIcon] = useHelpBox(helptext, name);
    const errorId = `${id}error`;
    return (
        <>
            <div className={joinNames(className, style.wrapper)} style={outerStyle}>
                <input
                    type="checkbox"
                    tabIndex={tabIndex}
                    name={name}
                    id={id}
                    {...attr}
                    aria-errormessage={errorId}
                    aria-invalid={error ? true : undefined}
                />
                <div className={style.labelWrapper}>
                    <Label htmlFor={id}>{label}</Label>
                    <HelpIcon />
                </div>
                <HelpBox />
            </div>
            {error && <ErrorMessage id={errorId}>{error}</ErrorMessage>}
        </>
    );
}

type FormikCheckBoxProps = Omit<CheckBoxProps, 'uiType'> & { checked?: boolean } & ValidValueProps;
export function FormikCheckBox({ name, label, defaultChecked, defaultValue, onValidValue, ...attr }: FormikCheckBoxProps): JSX.Element {
    const [{ onChange, onBlur, checked }, { touched, error }] = useField({
        name,
        type: 'checkbox',
        value: defaultValue,
    });
    const [verified, onInput] = useValidationFeedback(name, onValidValue);
    return (
        <CheckBox
            defaultValue={defaultValue}
            defaultChecked={defaultChecked}
            uistate={verified ? 'value-verified' : 'standard'}
            onChange={onChange}
            onBlur={onBlur}
            onInput={onInput}
            checked={checked}
            error={touched && error}
            name={name}
            label={label}
            {...attr}
        />
    );
}
